import { useState, useEffect, useContext } from "react";
import { StreamManager } from "openvidu-browser";
import { BroadcastUserDataDto, UserTypes } from "../../app/models/broadcast";
import BroadcastStreamingContainer from "./BroadcastStreamingContainer";
import { RootStoreContext } from "../../app/stores/rootStore";

interface BroadcastVideoPortraitProps {
    subscriber?: StreamManager,
    publisher?: StreamManager,
    userType: number,
    singleFullScreen?: string,
    fullScreen: boolean,
    toggleSingleFullScreen: (id?: string) => void,
    videoClassName: string,
}

const BroadcastVideoPortrait = ({ 
    subscriber, 
    publisher, 
    userType, 
    singleFullScreen, 
    fullScreen, 
    toggleSingleFullScreen, 
    videoClassName 
}: BroadcastVideoPortraitProps) => {
    const rootStore = useContext(RootStoreContext);
    const { grabbersList, broadcastUser, internalRaisedhands, externalRaisedhands, internalLoweredhands, externalLoweredhands, isCameraActive, isMicrophoneActive } = rootStore.broadcastStore;
    
    const [user, setUser] = useState<BroadcastUserDataDto | null>(null);

    useEffect(() => {
        if (subscriber) {
            const subscriberData = JSON.parse(subscriber.stream.connection.data);
            const allUsers = [...internalRaisedhands, ...externalRaisedhands, ...internalLoweredhands, ...externalLoweredhands, ...grabbersList];
            const subscriberIndex = allUsers.findIndex(user => 
                (user.userId && user.broadcastUserTypeId === subscriberData.userType && user.userId === subscriberData.userId) ||
                (user.grabberId && user.broadcastUserTypeId === subscriberData.userType && user.grabberId === subscriberData.userId)
            );

            if (subscriberIndex !== -1) {
                setUser(allUsers[subscriberIndex]);
            }
        }
    }, [subscriber, internalRaisedhands, externalRaisedhands, internalLoweredhands, externalLoweredhands, grabbersList]);

    const renderSubscriber = () => {
        if (!subscriber || !user) return null;

        const subscriberData = JSON.parse(subscriber.stream.connection.data);
        const isGrabber = subscriberData.userType === UserTypes.grabber;
        const isGrabberActive = grabbersList.some(grabber => grabber.grabberId === subscriberData.userId && grabber.broadcastUserTypeId === UserTypes.grabber);
        const key = isGrabber ? `grabber_${subscriberData.userId}` : `subscriber_${subscriberData.userId}`;

        if (broadcastUser.broadcastUserTypeId !== UserTypes.external) {
            if (!singleFullScreen || `Portrait_grabber_${subscriberData.userId}` === singleFullScreen) {
                if (isGrabber && isGrabberActive) {
                    return (
                        <BroadcastStreamingContainer
                            key={key}
                            user={subscriber}
                            mediaStream={subscriber.stream.getMediaStream()}
                            userId={subscriberData.userId.toString()}
                            fullScreen={singleFullScreen === `Portrait_grabber_${subscriberData.userId}` || fullScreen}
                            isGrabber={isGrabber}
                            toggleSingleFullScreen={toggleSingleFullScreen}
                            onlyVideo={true}
                            isMicrophoneActive={subscriber.stream.audioActive}
                            isCameraActive={subscriber.stream.videoActive}
                            videoClassName={videoClassName}
                            audioClassName={"audioStreamDiv"}
                            title={user.username || "Unknown User"}
                            handRaised={user.handRaisedPriority > 0}
                        />
                    );
                }
            }
        }
        if (!singleFullScreen || `Portrait${subscriberData.userId}` === singleFullScreen) {
            if (!isGrabber) {
                return (
                    <BroadcastStreamingContainer
                        key={key}
                        user={subscriber}
                        mediaStream={subscriber.stream.getMediaStream()}
                        userId={subscriberData.userId.toString()}
                        fullScreen={singleFullScreen === `Portrait${subscriberData.userId}` || fullScreen}
                        isGrabber={isGrabber}
                        toggleSingleFullScreen={toggleSingleFullScreen}
                        onlyVideo={true}
                        videoClassName={videoClassName}
                        audioClassName={"audioStreamDiv"}
                        title={user.username || "Unknown User"}
                        handRaised={user.handRaisedPriority > 0}
                        isCameraActive={subscriber.stream.videoActive}
                        isMicrophoneActive={subscriber.stream.audioActive}
                    />
                );
            }
        }
        return null;
    };

    const renderPublisher = () => {
        if (userType === UserTypes.internal || userType === UserTypes.external) return null;

        if (!publisher) return null;

        const publisherData = JSON.parse(publisher.stream.connection.data);
        if (!singleFullScreen || `Portrait${publisherData.userId}` === singleFullScreen) {
            return (
                <BroadcastStreamingContainer
                    key={`publisher_${publisherData.userId}`}
                    user={publisher}
                    mediaStream={publisher.stream.getMediaStream()}
                    userId={publisherData.userId.toString()}
                    fullScreen={singleFullScreen === `Portrait${publisherData.userId}` || fullScreen}
                    isGrabber={false}
                    toggleSingleFullScreen={toggleSingleFullScreen}
                    onlyVideo={true}
                    videoClassName={`publisher-div publisher stream-container ${singleFullScreen ? "publisherSingleFullScreen singleFullScreenPortrait" : fullScreen ? "" : "col-md-6 col-xs-6"}`}
                    audioClassName={"audioStreamDiv"}
                    title={broadcastUser.username || "Unknown User"}
                    handRaised={broadcastUser.handRaisedPriority > 0}
                    mirror={true}
                    isCameraActive={isCameraActive}
                    isMicrophoneActive={isMicrophoneActive}
                />
            );
        }
        return null;
    };

    return (
        <>
            {renderSubscriber()}
            {renderPublisher()}
        </>
    );
};

export default BroadcastVideoPortrait;